import { Fragment, useState } from 'react';
import { FiEdit2 } from 'react-icons/fi';
import { statusOptions, statusColors, countReturns, formatStatusLabel } from './ordersConstants';

const formatOrderMoney = (amount, order) => new Intl.NumberFormat(
  order.orderCurrencyCode === 'INR' ? 'en-IN' : 'en-US',
  { style: 'currency', currency: order.orderCurrencyCode || 'INR' }
).format(Number(amount) || 0);

const OrdersTable = ({
  orders,
  loading,
  selectedOrder,
  onSelect,
  onStatusChange,
}) => {
  const [shippingOrderId, setShippingOrderId] = useState(null);
  const [shippingDetails, setShippingDetails] = useState({ courier: '', trackingNumber: '' });
  const [savingShipment, setSavingShipment] = useState(false);

  const openShipmentEditor = (order) => {
    setShippingOrderId(order.orderId || order.id);
    setShippingDetails({
      courier: order.courier || '',
      trackingNumber: order.trackingNumber || '',
    });
  };

  const selectStatus = (order, status) => {
    const orderId = order.orderId || order.id;
    if (status === 'shipped' && order.status !== 'shipped') {
      openShipmentEditor(order);
      return;
    }
    setShippingOrderId(null);
    onStatusChange(orderId, status);
  };

  const confirmShipment = async (event, order) => {
    event.preventDefault();
    event.stopPropagation();
    if (!shippingDetails.courier.trim() || !shippingDetails.trackingNumber.trim()) return;
    setSavingShipment(true);
    const updated = await onStatusChange(order.orderId || order.id, 'shipped', {
      courier: shippingDetails.courier.trim(),
      trackingNumber: shippingDetails.trackingNumber.trim(),
    });
    setSavingShipment(false);
    if (updated) setShippingOrderId(null);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 text-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-black">
            <tr>
              <th className="px-4 py-3 text-left">Order ID</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Items</th>
              <th className="px-4 py-3 text-left">Total</th>
              <th className="px-4 py-3 text-left">Payment</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Returns</th>
              <th className="px-4 py-3 text-left">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {orders.map((order) => (
              <Fragment key={order.id}>
                <tr
                  className={`hover:bg-gray-50 cursor-pointer ${selectedOrder?.id === order.id ? 'bg-orange-50' : ''}`}
                  onClick={() => onSelect(order)}
                >
                  <td className="px-4 py-3 font-medium text-black">
                    #{order.orderId || order.id}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{order.user?.name || 'N/A'}</p>
                    <p className="text-xs text-black">{order.user?.email || ''}</p>
                  </td>
                  <td className="px-4 py-3">{order.items?.length || 0}</td>
                  <td className="px-4 py-3 font-medium">
                    {order.paymentMethod === 'paypal' && order.paypalAmountUsd && order.orderCurrencyCode !== 'USD' ? (
                      <>
                        <span>
                          ${parseFloat(order.paypalAmountUsd).toFixed(2)}{' '}
                          <span className="text-[10px] text-black align-middle">USD</span>
                        </span>
                        <span className="block text-xs font-normal text-black">
                          {formatOrderMoney(order.finalAmount || order.total, order)}
                        </span>
                      </>
                    ) : (
                      <span>{formatOrderMoney(order.finalAmount || order.total, order)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs capitalize">{order.paymentMethod}</span>
                    <span
                      className={`ml-1 text-xs px-1.5 py-0.5 rounded ${order.paymentStatus === 'paid'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-black'
                        }`}
                    >
                      {order.paymentStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex min-w-max flex-col items-start gap-1.5">
                      <select
                        value={order.status}
                        onChange={(e) => {
                          e.stopPropagation();
                          selectStatus(order, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className={`px-2 py-1 rounded text-xs font-medium border ${statusColors[order.status] || 'border-gray-300'}`}
                      >
                        {!statusOptions.includes(order.status) && (
                          <option value={order.status} disabled>
                            {formatStatusLabel(order.status)}
                          </option>
                        )}
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>
                            {formatStatusLabel(s)}
                          </option>
                        ))}
                      </select>
                      {order.status === 'shipped' && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openShipmentEditor(order);
                          }}
                          className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs font-medium text-orange-700 hover:bg-orange-50"
                        >
                          <FiEdit2 aria-hidden="true" />
                          Edit shipping
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const n = countReturns(order);
                      if (!n) return <span className="text-xs text-black">—</span>;
                      return (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border bg-orange-50 text-orange-700 border-orange-200">
                          {n} return{n > 1 ? 's' : ''}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-black text-xs">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                </tr>
                {shippingOrderId === (order.orderId || order.id) && (
                  <tr className="bg-orange-50/60">
                    <td colSpan={8} className="px-4 py-3">
                      <form
                        onSubmit={(event) => confirmShipment(event, order)}
                        onClick={(event) => event.stopPropagation()}
                        className="flex flex-col gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3 lg:flex-row lg:items-end"
                      >
                        <label className="min-w-0 flex-1 text-left text-xs font-medium text-gray-700">
                          Courier <span className="text-red-600">*</span>
                          <input
                            type="text"
                            value={shippingDetails.courier}
                            onChange={(event) => setShippingDetails((current) => ({
                              ...current, courier: event.target.value,
                            }))}
                            maxLength={100}
                            required
                            autoFocus
                            className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2 text-left text-sm"
                            placeholder="e.g. FedEx"
                          />
                        </label>
                        <label className="min-w-0 flex-1 text-left text-xs font-medium text-gray-700">
                          Tracking Number <span className="text-red-600">*</span>
                          <input
                            type="text"
                            value={shippingDetails.trackingNumber}
                            onChange={(event) => setShippingDetails((current) => ({
                              ...current, trackingNumber: event.target.value,
                            }))}
                            maxLength={100}
                            required
                            className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2 text-left text-sm"
                            placeholder="Enter tracking number"
                          />
                        </label>
                        <div className="flex shrink-0 justify-end gap-2 lg:pb-px">
                          <button
                            type="button"
                            onClick={() => setShippingOrderId(null)}
                            disabled={savingShipment}
                            className="rounded border border-gray-300 bg-white px-3 py-2 text-xs"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={savingShipment || !shippingDetails.courier.trim() || !shippingDetails.trackingNumber.trim()}
                            className="rounded bg-orange-600 px-3 py-2 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {savingShipment
                              ? 'Saving...'
                              : order.status === 'shipped'
                                ? 'Update Shipping'
                                : 'Confirm Shipped'}
                          </button>
                        </div>
                      </form>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-8 text-black">
                  No orders found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrdersTable;
